import * as _ from 'underscore';
import { IAuthenticationProviderSelectorModel } from '../directives/tbAuthenticationProviderSelector';
import { ListResult } from '../fetchers/ListResult';
import { IActionContext } from '../helpers/ActionContext';
import { CommonErrorCode } from '../helpers/ErrorCodes';
import { L10n } from '../L10n/L10n';
import { IErrorItem, MultipleItemResult } from '../models/MultipleItemResult';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { SiteRolesResource } from '../../userManagement/services/resources/SiteRoles';
import { SiteUsers } from '../resources/SiteUsers';
import { IError } from '../ServerApi/types/IError';
import { IResult } from '../ServerApi/types/IResponse';
import { ISiteUser } from '../ServerApi/types/ISiteUser';
import { ConfirmActionDialog, IConfirmActionDialogInstance } from './ConfirmActionDialog';
import { ContentActionNotification, MultipleResultNotificationOptions } from './ContentActionNotification';
import { ModalService } from './ModalService';
import { ServerService } from './ServerService';
import {
  AuthenticationProviderType,
  IAuthenticationProvider,
  SiteAuthenticationSettingsService,
} from './SiteAuthenticationSettingsService';

export class UpdateUserAuthenticationAction {

  public static $inject = [
    '$q',
    '$translate',
    'SiteAuthenticationSettingsService',
    'ConfirmActionDialog',
    'ServerService',
    'ContentActionNotification',
    'SiteUsers',
  ];

  constructor(
    private $q: ng.IQService,
    private $translate: ng.translate.ITranslateService,
    private siteAuthenticationSettingsService: SiteAuthenticationSettingsService,
    private confirmActionDialog: ConfirmActionDialog,
    private server: ServerService,
    private contentActionNotification: ContentActionNotification,
    private siteUsers: SiteUsers,
  ) { }

  public showUpdateUserAuthenticationDialog(
    siteUsersToUpdate: Set<ISiteUser>,
    context: IActionContext): ng.IPromise<any> {

    const scope = <IDialogScope> context.scope.$new();
    scope.authenticationProviderSelectorModel = {
      getProviderDescription: (provider: IAuthenticationProvider) => {
        const translateValues = {
          SERVER_NAME: this.server.serverName(),
          PROVIDER_NAME: this.siteAuthenticationSettingsService.getProviderDisplayName(provider),
        };
        const translateKey = (provider.type === AuthenticationProviderType.default) ?
          'setUsersAuthentication_description_internal':
          'setUsersAuthentication_description_external';
        return this.$translate.instant(translateKey, translateValues);
      },
      authenticationProviderIds: _.map(siteUsersToUpdate.toArray(), (user: ISiteUser) => user.authenticationProviderId)
    };

    const currentUser = siteUsersToUpdate.get(this.server.sessionUserInfo().id);
    let noChanges = true;
    scope.$watch('authenticationProviderSelectorModel.authenticationProvider', (newValue: IAuthenticationProvider, oldValue: IAuthenticationProvider) => {
      if (newValue === oldValue) return;
      noChanges = false;
      scope.isAboutToBeLoggedOut = currentUser && currentUser.authenticationProviderId !== newValue.id;
    });

    const userIds = _.map(siteUsersToUpdate.toArray(), (user: ISiteUser) => user.id);

    const dialog = this.confirmActionDialog.open({
      title: this.$translate.instant('updateUserAuthenticationTitle'),
      bodyTemplateUrl: 'updateUserAuthenticationDialog_v2.html',
      scope: scope,
      position: ModalService.anchoredTo(context.targetElement),
      actionName: this.$translate.instant('update'),
      action: (confirmActionDialogInstance: IConfirmActionDialogInstance) => {
        const provider = scope.authenticationProviderSelectorModel.authenticationProvider;
        const updateUserAuthentication = () => {
          const promise = this.siteAuthenticationSettingsService.setProviderIdForUsers(provider.id, userIds);
          this.showNotification(siteUsersToUpdate, promise);
          return promise;
        };
        return this.determineIfNoMoreAdminsUsingDefaultProvider(siteUsersToUpdate, provider).then((showWarning: boolean) => {
          if (showWarning) {
            const warningDialog = this.confirmActionDialog.open({
              title: this.$translate.instant('updateUserAuthenticationTitle'),
              bodyTemplateUrl: 'updateUserAuthenticationDialogWarning.html',
              isDeleteAction: true,
              actionName: this.$translate.instant('update'),
              action: updateUserAuthentication,
            });
            return warningDialog.result;
          } else {
            return updateUserAuthentication();
          }
        });
      },
      confirmDisabled: () => noChanges
    });
    return dialog.result;
  }

  private showNotification(siteUsersToUpdate: Set<ISiteUser>, promise: ng.IPromise<IResult>) {
    promise.catch(() => {
      this.contentActionNotification.notifyFailure(this.$translate.instant('unexpectedError'));
    });
    promise.then((result: IResult) => {
      const multipleItemResult = new MultipleItemResult(siteUsersToUpdate, result);
      this.contentActionNotification.notify(MultipleResultNotificationOptions.create(multipleItemResult,
        {
          describeSuccessSummary: (successCount: number) =>
            this.$translate.instant('updateUserAuthenticationSuccessSummary', {COUNT: successCount}, L10n.Interpolations.Count),

          describeErrorSummary: (errorCount: number) =>
            this.$translate.instant('updateUserAuthenticationErrorSummary', {COUNT: errorCount}, L10n.Interpolations.Count),

          describeErrorDetail: (errorItem: IErrorItem<ISiteUser>) => {
            return {
              itemName: errorItem.item && errorItem.item.displayName,
              itemUrl: null,
              errorMessage: this.getErrorDetailText(errorItem.error)
            };
          }
        }));
    });
  }

  private getErrorDetailText(error: IError) {
    switch(error.code) {
      case CommonErrorCode.SiteSAMLUserExistsOnAnotherSite:
        return this.$translate.instant('userNameExistsOnServerForUpdateAuthenticationToSiteSAML');
      case CommonErrorCode.SystemUserCannotBeSiteSAMLUser:
        return this.$translate.instant('systemAdminNotAllowedAsSiteSAMLUser');
      case CommonErrorCode.GuestUserCannotBeSiteSAMLUser:
        return this.$translate.instant('guestUserNotAllowedAsSiteSAMLUser');
      default:
        return this.$translate.instant('unexpectedError');
    }
  }

  private determineIfNoMoreAdminsUsingDefaultProvider(siteUsersToUpdate: Set<ISiteUser>, newAuthenticationProvider: IAuthenticationProvider): ng.IPromise<boolean> {
    const defaultAuthenticationProviderId = this.siteAuthenticationSettingsService.getDefaultProvider().id;
    const authenticatedByDefaultProvider = (user: ISiteUser) => user.authenticationProviderId === defaultAuthenticationProviderId;

    if (newAuthenticationProvider.id === defaultAuthenticationProviderId) {
      return this.$q.resolve(false);
    }
    return this.siteUsers.getUsersBySiteRoles(SiteRolesResource.instance.getAdminRoles()).then((result: ListResult<ISiteUser>) => {
      let adminsUsingDefaultProviderBefore = Set.fromIdItemArray(result.items).filter(authenticatedByDefaultProvider);
      let adminsUsingDefaultProviderAfter = adminsUsingDefaultProviderBefore.clone();
      adminsUsingDefaultProviderAfter.removeItemsIn(siteUsersToUpdate);
      return !adminsUsingDefaultProviderBefore.isEmpty() && adminsUsingDefaultProviderAfter.isEmpty();
    });
  }
}

Module.instance().service('UpdateUserAuthenticationAction', UpdateUserAuthenticationAction);

interface IDialogScope extends ng.IScope {
  authenticationProviderSelectorModel: IAuthenticationProviderSelectorModel;
  isAboutToBeLoggedOut: boolean;
}
